import { PaymentStatusOption, PaymentLifecycleView } from "helpers/paymentLifecycle";
import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";

import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

export interface WithdrawalItem extends PaymentLifecycleView {
  origin?: "user" | "backoffice_manual";
  id: number;
  userId: number;
  amount: number;
  fee: number;
  currency: string;
  paymentMethod?: string;
  status: string;
  created: string;
  completedAt?: string | null;
  externalRef?: string | null;
  processingType?: string;
  destinationAmount?: number;
  destinationCurrency?: string;

  paymentProviderTitle?: string;
  paymentProviderImageCdn?: string;

  paymentMethodTitle?: string;
  paymentMethodImageCdn?: string;

  destination?: string;
}

interface WithdrawalsState {
  list: WithdrawalItem[];
  activePage: number;
  totalPages: number;
  totalItems: number;
  isListFetched: boolean;
  selectedItems: number[];
  statusesList: PaymentStatusOption[];
  isStatusesFetched: boolean;
  processingItemId?: number;
}

interface GetWithdrawalsPayload {
  limit: number;
  offset: number;

  userIds?: number[];
  currencies?: string[];
  canonical_status?: string[];
  paymentMethod?: string;
  externalRef?: string;
  createdAtFrom?: string;
  createdAtTo?: string;
  completedAtFrom?: string;
  completedAtTo?: string;

  order: {
    id?: string;
    userId?: string;
    amount?: string;
    currency?: string;
    paymentMethod?: string;
    status?: string;
    created?: string;
  };
}

const initialState: WithdrawalsState = {
  list: [],
  isListFetched: false,
  activePage: 1,
  totalItems: 0,
  totalPages: 0,
  selectedItems: [],
  statusesList: [],
  isStatusesFetched: false,
};

export const getWithdrawalsList = createAsyncThunk(
  "paymentsWithdrawals/list",
  async (data: GetWithdrawalsPayload) => {
    try {
      const result = await instance.post(
        apiRoutes.paymentWithdrawalsList,
        data,
      );

      return result.data;
    } catch (error) {
      return undefined;
    }
  },
);

export const getWithdrawalStatusesList = createAsyncThunk(
  "paymentsWithdrawals/statuses",
  async () => {
    try {
      const result = await instance.get(
        `${apiRoutes.paymentWithdrawalStatuses}?limit=500&offset=0&canonical=true`,
      );

      return result.data.items;
    } catch (error) {
      return [];
    }
  },
);

export const modifyWithdrawalStatus = createAsyncThunk(
  "paymentsWithdrawals/modifyStatus",
  async (
    { id, statusCode }: { id: number; statusCode: string },
    { rejectWithValue },
  ) => {
    try {
      const result = await instance.put(
        `${apiRoutes.paymentWithdrawalModifyStatus}/${id}`,
        {
          statusCode,
        },
      );

      return result.data;
    } catch (error: any) {
      const data = error?.data || error?.response?.data;

      const apiErrors = data?.errors;

      if (Array.isArray(apiErrors)) {
        return rejectWithValue({
          code: "BusinessError",
          fieldErrors: [],
          globalErrors: apiErrors.map((item: { message: string }) => ({
            code: item.message,
          })),
        });
      }

      return rejectWithValue({
        code: "InternalError",
        fieldErrors: [],
        globalErrors: [{ code: "unknownError" }],
      });
    }
  },
);

export const paymentsWithdrawalsSlice = createSlice({
  name: "paymentsWithdrawals",
  initialState,
  reducers: {
    handlePaginate: (state, action: PayloadAction<number>) => {
      state.activePage = action.payload;
      state.isListFetched = false;
    },
    handleSelectItem: (state, action: PayloadAction<number>) => {
      if (state.selectedItems.includes(action.payload)) {
        state.selectedItems = state.selectedItems.filter(
          (el) => el !== action.payload,
        );
      } else {
        state.selectedItems = [...state.selectedItems, action.payload];
      }
    },
    handleSelectAll: (state) => {
      if (state.selectedItems.length === state.list.length) {
        state.selectedItems = [];
      } else {
        state.selectedItems = state.list.map((el) => el.id);
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getWithdrawalsList.fulfilled, (state, action) => {
      state.isListFetched = true;

      state.list = action.payload?.items ?? [];
      state.totalItems = action.payload?.totalItems ?? 0;
      state.totalPages = action.payload?.totalPages ?? 0;
    });
    builder.addCase(getWithdrawalStatusesList.fulfilled, (state, action) => {
      state.statusesList = action.payload.map((el: any) => ({
        value: el.code,
          statusId: el.statusId,
        label: el.title,
      }));
      state.isStatusesFetched = true;
    });
    builder.addCase(modifyWithdrawalStatus.pending, (state, action) => {
      state.processingItemId = action.meta.arg.id;
    });
    builder.addCase(modifyWithdrawalStatus.fulfilled, (state, action) => {
      state.processingItemId = undefined;

      const updated = action.payload?.data;

      if (!updated) return;

      const index = state.list.findIndex((el) => el.id === updated.id);

      if (index !== -1) {
        state.list[index].status = updated.status;
        state.list[index].canonical_status = updated.canonical_status;
        state.list[index].allowed_status_actions = updated.allowed_status_actions ?? [];
      }
    });
    builder.addCase(modifyWithdrawalStatus.rejected, (state) => {
      state.processingItemId = undefined;
    });
  },
});

export const { handlePaginate, handleSelectAll, handleSelectItem } =
  paymentsWithdrawalsSlice.actions;
